import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { ModalController } from '@ionic/angular';
import { SubscriberService } from 'src/app/services/manager/subscriber.service';
import { Order, Address, LexwareContact } from 'src/app/interfaces/orders';
import { AlertService } from 'src/app/services/base/alert.service';
import { OfflineService } from 'src/app/services/manager/offline.service';
import { ModalService } from 'src/app/services/base/modal.service';
import { CurrentOrderService, ORDER_CLOSED_MESSAGE } from 'src/app/services/manager/current-order.service';
import { OrderService } from 'src/app/services/manager/order.service';
import { CoreService } from 'src/app/services/manager/core.service';
import { ReportService } from 'src/app/services/manager/report.service';
import { LoginService } from 'src/app/services/auth/login.service';
import { SessionService } from 'src/app/services/manager/session.service';

import { ChangeAddressComponent } from '../change-address/change-address.component';
import { LexwareContactComponent } from '../../contact/lexware-contact/lexware-contact.component';
import { DeliveryManagerComponent } from '../delivery-manager/delivery-manager.component';
import { OrderHistoryComponent } from '../order-history/order-history.component';

@Component({
  selector: 'app-current-order',
  templateUrl: './current-order.component.html',
  styleUrls: ['./current-order.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrentOrderComponent implements OnInit, OnDestroy {
  destroy = new Subject();

  get userdata() { return this.login.decodedToken!; }

  searchTerm = '';

  order: Order | null = null;
  dataListFiltered: any;
  hasUnsavedChanges = false;
  private originalOrderSnapshot: string = '';

  addressEditable = false;
  karmaSending = false;
  karmaKuerzelMode: 'user' | 'customer' = 'user';
  karmaCartRef: { label: string; url: string | null } | null = null;
  karmaPaymentId = '59';
  karmaDeliveryDate = '';
  karmaEmail = '';
  karmaPhone = '';
  karmaNote = '';

  readonly karmaPaymentOptions = [
    { id: '11', label: 'Banküberweisung im Voraus' },
    { id: '20', label: 'Nachnahme 30 T. MF' },
    { id: '21', label: 'Nachnahme 30/60 T. MF' },
    { id: '22', label: 'Nachnahme 30/60/90 T. MF' },
    { id: '26', label: 'Nachnahme 60 T. MF' },
    { id: '59', label: 'Direktüberweisung' },
  ];

  get userKuerzel(): string | null {
    return this.login.decodedToken?.kuerzel ?? null;
  }

  get cartItemCount(): number {
    const products = this.order?.products;
    if (!Array.isArray(products)) return 0;
    return products.reduce((sum: number, p: any) => {
      const qty = Array.isArray(p.sizes)
        ? p.sizes.reduce((s: number, sz: any) => s + (Number(sz.quantity) || 0), 0)
        : 0;
      return sum + qty;
    }, 0);
  }

  get cartTotal(): number {
    const products = this.order?.products;
    if (!Array.isArray(products)) return 0;
    return products.reduce((sum: number, p: any) => {
      const qty = Array.isArray(p.sizes)
        ? p.sizes.reduce((s: number, sz: any) => s + (Number(sz.quantity) || 0), 0)
        : 0;
      return sum + qty * (Number(p.price) || 0);
    }, 0);
  }

  // Inline customer search
  customerSearchTerm = '';
  private customerSearchSubject = new Subject<string>();
  lexwareContacts: any[] = [];
  lexwareContactsFiltered: any[] = [];
  readonly customerResultsLimit = 20;

  // Inline product search
  productSearchTerm = '';
  private productSearchSubject = new Subject<string>();
  productList: any[] = [];
  productListFiltered: any[] = [];
  readonly productResultsLimit = 20;

  selectedSeason: string = 'all';
  availableSeasons: string[] = [];

  constructor(
    private sub: SubscriberService,
    private subscriberService: SubscriberService,
    private alert: AlertService,
    private offline: OfflineService,
    private modals: ModalService,
    private current: CurrentOrderService,
    private orderService: OrderService,
    private core: CoreService,
    private report: ReportService,
    private cdr: ChangeDetectorRef,
    private login: LoginService,
    private session: SessionService,
    private modalController: ModalController
  ) {}

  async ngOnInit() {
    // Stelle sicher, dass SessionService die Defaults geladen hat
    // (nicht awaiten, damit das Öffnen einer Order nicht auf Netzwerk-Requests wartet)
    this.session.loadDefaults();

    // Ensure lists exist for inline search.
    // Important: avoid triggering heavy loads/refreshes on every order open.
    // Only load if not already present.
    if ((this.sub.productList.getValue() || []).length === 0) {
      this.core.loadProducts();
    }
    if ((this.sub.lexwareContactList.getValue() || []).length === 0) {
      this.core.loadLexwareContacts();
    }

    this.customerSearchSubject.pipe(
      takeUntil(this.destroy),
      debounceTime(250),
      distinctUntilChanged()
    ).subscribe(term => {
      this.customerSearchTerm = term;
      this.filterCustomers();
      this.cdr.markForCheck();
    });

    this.productSearchSubject.pipe(
      takeUntil(this.destroy),
      debounceTime(250),
      distinctUntilChanged()
    ).subscribe(term => {
      this.productSearchTerm = term;
      this.filterProductsInline();
      this.cdr.markForCheck();
    });

    this.session.karmaDefaults$
      .pipe(takeUntil(this.destroy))
      .subscribe(defaults => {
        if (this.order) {
          if (!this.karmaPhone) this.karmaPhone = defaults.phone;
          if (!this.karmaEmail) this.karmaEmail = defaults.email;
          if (!this.karmaDeliveryDate) this.karmaDeliveryDate = defaults.deliveryDate;
          if (!this.karmaPaymentId || this.karmaPaymentId === '59') this.karmaPaymentId = defaults.paymentId || '59';
          if (!this.karmaNote) this.karmaNote = defaults.note;
          this.cdr.markForCheck();
        }
      });

    this.subscribe();
  }

  ngOnDestroy() {
    this.destroy.next(null);
    this.destroy.complete();
  }

  subscribe() {
    this.sub.currentOrder
      .pipe(takeUntil(this.destroy))
      .subscribe((data: Order) => {
        const isNewOrder = !this.order || this.order.orderId !== data?.orderId;
        this.order = data;

        // Nur bei neuem Order Defaults laden
        if (isNewOrder && this.order) {
          const d = this.session.karmaDefaults$.getValue();
          this.karmaPhone = d.phone;
          this.karmaEmail = d.email;
          this.karmaDeliveryDate = d.deliveryDate;
          this.karmaPaymentId = d.paymentId || '59';
          this.karmaNote = d.note;
          this.loadDefaultValuesIfEmpty();

          // Aktualisiere Titel mit Kundenname, falls vorhanden
          if (this.order.customerAddress?.name) {
            this.updateOrderTitleWithCustomerName(
              this.order.customerAddress.name
            );
          }

          // Erstelle Snapshot NACH dem Laden der Default-Werte
          // So werden die Defaults nicht als Änderung erkannt
          this.originalOrderSnapshot = JSON.stringify(this.order);
          this.hasUnsavedChanges = false;
        } else if (this.order) {
          // Prüfe ob Änderungen vorhanden sind (z.B. bei Produktänderungen)
          this.checkForChanges();
        }

        this.cdr.markForCheck();
      });

    this.sub.lexwareContactList
      .pipe(takeUntil(this.destroy))
      .subscribe((data: any[]) => {
        this.lexwareContacts = Array.isArray(data) ? data : [];
        this.filterCustomers();
        this.cdr.markForCheck();
      });

    this.sub.productList
      .pipe(takeUntil(this.destroy))
      .subscribe((data: any[]) => {
        this.productList = Array.isArray(data) ? data : [];
        this.extractAvailableSeasons();
        this.filterProductsInline();
        this.cdr.markForCheck();
      });
  }

  changeSelectedSeason(season: string) {
    this.selectedSeason = season;
    this.filterProductsInline();
    this.cdr.markForCheck();
  }

  private extractAvailableSeasons() {
    if (!this.productList || this.productList.length === 0) {
      this.availableSeasons = [];
      return;
    }

    const uniqueSeasons = [...new Set(this.productList.map((p: any) => p?.season))]
      .filter((s) => s)
      .sort();
    this.availableSeasons = uniqueSeasons;
  }

  onCustomerSearchChange(event: any) {
    const inputValue = event.target.value;
    this.customerSearchSubject.next(inputValue);
  }

  private filterCustomers() {
    const base = this.lexwareContacts || [];
    const term = (this.customerSearchTerm || '').toLowerCase().trim();
    if (!term) {
      this.lexwareContactsFiltered = [];
      return;
    }

    this.lexwareContactsFiltered = base
      .filter((item: any) => {
        const fields = [
          item?.companyName,
          item?.email,
          item?.phone,
          item?.address?.street,
          item?.address?.city,
          item?.address?.zip,
          item?.contactPerson?.firstName,
          item?.contactPerson?.lastName,
          item?.contactPerson?.emailAddress,
          item?.contactPerson?.phoneNumber
        ];

        return fields.some(v => v != null && v.toString().toLowerCase().includes(term));
      })
      .slice(0, this.customerResultsLimit);
  }

  selectCustomerInline(contact: any) {
    this.current.setLexwareContact(contact);
    this.customerSearchTerm = '';
    this.customerSearchSubject.next('');
  }

  trackByCustomerId(index: number, item: any): string {
    return item?.id || item?.email || item?.companyName || index.toString();
  }

  onProductSearchChange(event: any) {
    const inputValue = event.target.value;
    this.productSearchSubject.next(inputValue);
  }

  private filterProductsInline() {
    let base = this.productList || [];

    if (this.selectedSeason !== 'all') {
      base = base.filter((p: any) => p?.season === this.selectedSeason);
    }

    const term = (this.productSearchTerm || '').toLowerCase().trim();
    if (!term) {
      this.productListFiltered = [];
      return;
    }

    const searchableFields = ['name', 'model', 'season', 'color', 'colorname'];
    this.productListFiltered = base
      .filter((item: any) => {
        return searchableFields.some(field => {
          const value = item?.[field];
          return value != null && value.toString().toLowerCase().includes(term);
        });
      })
      .slice(0, this.productResultsLimit);
  }

  openProductFromSearch(product: any) {
    if (!product) {
      return;
    }
    // Die Suche ist bei abgeschlossenem Auftrag ausgeblendet — der Guard fängt
    // verbleibende Aufrufe ab, damit das Modal gar nicht erst aufgeht.
    if (!this.current.canModifyProducts()) {
      this.alert.presentToast(ORDER_CLOSED_MESSAGE);
      return;
    }
    const sameModel = (this.productList || []).filter((p: any) => p?.model === product?.model);
    this.orderService.openProductModal(sameModel);
  }

  trackByProductSearchId(index: number, item: any): string {
    return item?.modelId || item?.id || item?.model || index.toString();
  }

  // Email zu Name Mapping als Klassen-Property (wird nur einmal erstellt)
  private readonly emailToNameMap: Record<string, string> = {
    'info@anja-faustmann.de': 'Anja',
    'brita@daberkow-mode.de': 'Brita Daberkow',
    'info@modeagentur-weisz.de': 'Stephanie Weisz',
    'sanz@modeagentur-sanz.de': 'Susanne Sanz',
    'wolfgang.bloemacher@gmx.de': 'Wolfgang Blömacher'
  };

  // Lade Default-Werte aus SessionService, wenn Felder leer sind
  loadDefaultValuesIfEmpty() {
    if (!this.order) return;

    const isAdmin = this.userdata.role === 'admin';

    // Helper: Check if string is empty
    const isEmpty = (str: string | null | undefined) => !str || str.trim() === '';

    // Firmeninfo aus Default laden, wenn leer
    if (isEmpty(this.order.companyName)) {
      const defaultCompanyInfo = this.session.getDefaultCompanyInfo();
      if (defaultCompanyInfo && (this.order.companyName == null || !isAdmin)) {
        this.order.companyName = defaultCompanyInfo;
      }
    }

    // Agenturinfo aus Default laden, wenn leer
    if (isEmpty(this.order.agentNote)) {
      const defaultAgentNote = this.session.getDefaultAgentNote();
      if (defaultAgentNote && (this.order.agentNote == null || !isAdmin)) {
        if (this.order.owner !== 'NEW' && !isAdmin) {
          this.order.agentNote = `${defaultAgentNote} ${this.order.owner.username}`;
        } else if (this.order.owner === 'NEW') {
          const userName = this.emailToNameMap[this.userdata.email] || this.userdata.email;
          this.order.agentNote = `${defaultAgentNote} ${userName}`;
        }
      }
    }

    // Lieferzeit aus Default laden, wenn leer
    if (isEmpty(this.order.deliveryNote)) {
      const defaultDeliveryTime = this.session.getDefaultDeliveryTime();
      if (defaultDeliveryTime && (this.order.deliveryNote == null || !isAdmin)) {
        this.order.deliveryNote = defaultDeliveryTime;
      }
    }

    // Keine updateOrder() hier - die Werte werden nur lokal gesetzt
    // Der Snapshot wird nach dieser Methode erstellt und enthält die Default-Werte
  }

  private checkForChanges() {
    if (!this.order || !this.originalOrderSnapshot) return;
    const currentSnapshot = JSON.stringify(this.order);
    this.hasUnsavedChanges = currentSnapshot !== this.originalOrderSnapshot;
  }

  async onSave() {
    await this.current.saveOrder();
    if (this.order) {
      this.originalOrderSnapshot = JSON.stringify(this.order);
      this.hasUnsavedChanges = false;
      this.cdr.markForCheck();
    }
  }

  async onExit() {
    // Übergebe ob gespeichert werden soll basierend auf hasUnsavedChanges
    await this.current.saveAndExit(this.hasUnsavedChanges);
    //TODO: AUTO UPLOAD
    // syncOfflineOrders wird bereits in saveAndExit aufgerufen
    this.hasUnsavedChanges = false;
  }

  async justSave() {
    await this.current.justSave();
    if (this.order) {
      this.originalOrderSnapshot = JSON.stringify(this.order);
      this.hasUnsavedChanges = false;
      this.cdr.markForCheck();
    }
  }

  async resetAndExit() {
    await this.current.resetAndExit();
  }

  async forceExit() {
    await this.current.forceExit();
  }

  async chooseLexwareContact() {
    await this.current.chooseLexwareContact();
  }

  async changeAddress() {
    await this.current.changeAddress();
  }

  resetContact() {
    this.current.resetContact();
  }

  // Check if delivery address is different from billing address and not empty
  hasDistinctDeliveryAddress(): boolean {
    if (!this.order?.customerBase?.delivery || !this.order?.customerAddress) {
      return false;
    }

    const delivery = this.order.customerBase.delivery;

    // Check if delivery address has any actual content
    const deliveryName = delivery.companyName?.trim() || '';
    const deliveryStreet = delivery.street?.trim() || '';
    const deliveryCity = delivery.city?.trim() || '';
    const deliveryZip = delivery.zip?.trim() || '';
    const deliverySupplement = delivery.supplement?.trim() || '';

    // Return false if delivery address is completely empty
    const hasDeliveryContent =
      deliveryName ||
      deliveryStreet ||
      deliveryCity ||
      deliveryZip ||
      deliverySupplement;
    if (!hasDeliveryContent) {
      return false;
    }

    // If we have delivery content, compare with billing address
    const billing = this.order.customerAddress;
    const billingName = billing.name?.trim() || '';
    const billingStreet = billing.street?.trim() || '';
    const billingCity = billing.city?.trim() || '';
    const billingZip = billing.zip?.trim() || '';
    const billingSupplement = billing.supplement?.trim() || '';

    // Return true if any field is different
    return (
      deliveryName !== billingName ||
      deliveryStreet !== billingStreet ||
      deliveryCity !== billingCity ||
      deliveryZip !== billingZip ||
      deliverySupplement !== billingSupplement
    );
  }

  async createDeliveryNote(order: any) {
    if (order.customerAddress === null) {
      this.alert.presentToast('Es wurde kein Kunde ausgewählt');
      return;
    }

    this.orderService.createDiliveryNote(order);
  }

  async createInvoice(order: any) {
    const noCustomer = order.customerAddress === null;
    const noProducts = !order.products || order.products.length === 0;

    if (noCustomer || noProducts) {
      const reason = noCustomer && noProducts
        ? 'kein Kunde ausgewählt und keine Produkte vorhanden'
        : noCustomer
          ? 'kein Kunde ausgewählt'
          : 'keine Produkte vorhanden';
      const confirmed = await this.alert.presentConfirmationAlert(
        'Unvollständige Rechnungsdaten',
        `Für diesen Auftrag ist ${reason}. Trotzdem eine Rechnung erstellen?`
      );
      if (!confirmed) return;
    }

    this.orderService.createInvoice(order);
  }

  async sendToKarma() {
    if (!this.order) return;
    this.karmaSending = true;
    this.cdr.markForCheck();
    try {
      const ref = await this.orderService.sendToKarma(this.order, this.karmaKuerzelMode, {
        paymentId: this.karmaPaymentId,
        deliveryDate: this.karmaDeliveryDate,
        email: this.karmaEmail,
        phone: this.karmaPhone,
        note: this.karmaNote,
      });
      if (ref) {
        this.karmaCartRef = ref;
        this.cdr.markForCheck();
      }
    } finally {
      this.karmaSending = false;
      this.cdr.markForCheck();
    }
  }

  async openDeliveryManager() {
    if (!this.order) {
      this.alert.presentToast('Kein Auftrag vorhanden');
      return;
    }

    const modal = await this.modalController.create({
      component: DeliveryManagerComponent,
      componentProps: { order: this.order },
      cssClass: 'modal-full',
    });

    await modal.present();
  }

  getUpdatedByName(updatedBy: any): string {
    if (!updatedBy) return 'Unbekannt';
    if (typeof updatedBy === 'string') return updatedBy;
    if (updatedBy.username) return updatedBy.username;
    if (updatedBy.email) return updatedBy.email;
    return updatedBy.id || 'Unbekannt';
  }

  async openOrderHistory() {
    if (!this.order) {
      this.alert.presentToast('Kein Auftrag vorhanden');
      return;
    }

    const modal = await this.modalController.create({
      component: OrderHistoryComponent,
      componentProps: {
        orderId: this.order.orderId,
        orderNumber: this.order.orderNumber,
      },
      cssClass: 'modal-full',
    });

    await modal.present();
  }

  exportOrderJson() {
    if (!this.order) return;
    const json = JSON.stringify(this.order, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `order_${this.order.orderId}_${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async showRevisions() {
    if (!this.order?.orderId) return;
    const revisions = await this.offline.getOrderRevisions(this.order.orderId);
    if (!revisions.length) {
      this.alert.presentToast('Keine lokalen Revisionen vorhanden');
      return;
    }
    const buttons = revisions.map((rev, i) => ({
      text: `${new Date(rev.timestamp).toLocaleString('de-DE')}`,
      handler: async () => {
        const confirmed = await this.alert.presentConfirmationAlert(
          'Revision wiederherstellen',
          `Revision vom ${new Date(rev.timestamp).toLocaleString('de-DE')} laden? Ungespeicherte Änderungen gehen verloren.`
        );
        if (confirmed) {
          await this.current.updateOrder(rev.order);
          this.alert.presentToast('Revision wiederhergestellt');
        }
      },
    }));
    buttons.push({ text: 'Abbrechen', role: 'cancel' } as any);
    this.alert.presentActionSheet('Lokale Revisionen', buttons);
  }

  async temp() {
    console.log(this.order);
    this.alert.presentToast('Absender E-Mail nicht vorhanden');
  }

  /**
   * Synct den aktuellen Auftrag vor dem PDF-Versand zum Server — ansonsten koennte
   * das PDF einen lokal veralteten Stand abbilden, waehrend der eigentliche
   * Server-Sync erst spaeter ueber den Retry-Loop nachzieht. Schlaegt der Sync fehl,
   * muss der Nutzer bewusst bestaetigen, dass trotzdem versendet werden soll.
   */
  private async ensureSyncedBeforePdf(): Promise<boolean> {
    if (!this.order) return false;

    await this.current.justSave();
    await this.orderService.syncOfflineOrders(true, 'pdfSend');

    const stillOffline = this.subscriberService.offlineOrders
      .getValue()
      .some(o => o.orderId === this.order!.orderId);

    if (stillOffline) {
      return this.alert.presentConfirmationAlert(
        'Synchronisierung fehlgeschlagen',
        'Der Auftrag konnte nicht mit dem Server synchronisiert werden. Das PDF würde dann einen lokal ungesicherten Stand abbilden.\n\nTrotzdem fortfahren und PDF versenden?',
        'Trotzdem senden',
        'Abbrechen'
      );
    }

    return true;
  }

  async sendconfirm(
    adminEmail?: string,
    customerEmail?: string,
    customerNeedEmail: boolean = false
  ) {
    let targetEmail = '';
    let isCustomerEmail = false;

    if (this.order?.owner == 'NEW') {
      this.alert.presentToast(
        'Syncronisierung ausstehend - bitte speichern Sie den Auftrag zuerst und öffnen Sie wenn es eine Order Number vorhanden ist'
      );
      return;
    }

    // Determine which email to use
    if (customerEmail) {
      targetEmail = customerEmail;
      isCustomerEmail = true;
    } else if (adminEmail) {
      targetEmail = adminEmail;
      isCustomerEmail = false;
    } else {
      this.alert.presentToast('Keine E-Mail-Adresse angegeben');
      return;
    }

    // Check if email is present
    if (!targetEmail || targetEmail.trim() === '') {
      this.alert.presentToast('Empfänger E-Mail nicht vorhanden');
      return;
    }

    // Check if email is valid
    const email = targetEmail.trim();
    if (this.isValidEmail(email) === false) {
      const corr = await this.alert.presentConfirmationAlert(
        'Ungültige E-Mail',
        email + ' enthält ein ungültiges Format. Trotzdem senden?'
      );
      if (corr === false) {
        return;
      }
    }

    // Question if report should be sent
    const recipientType = isCustomerEmail ? 'Kunden' : 'Admin';
    const wee = await this.alert.presentConfirmationAlert(
      `Auftrag an ${recipientType} senden?`,
      `Report an den ${recipientType} senden: ` + email
    );
    if (wee === false) {
      return;
    }

    if (!(await this.ensureSyncedBeforePdf())) {
      return;
    }

    // Create a copy of the order with the target email
    const orderCopy = JSON.parse(JSON.stringify(this.order));

    orderCopy.customerEmail = customerEmail;
    orderCopy.adminEmail = adminEmail;


    this.report.createSendToCustomer(orderCopy, customerEmail, adminEmail);
  }

  async sendconfirmAlternate(reciever: string = '') {
    const sendToCustomer = true;
    if (!this.order) {
      this.alert.presentToast('Kein Auftrag vorhanden');
      return;
    }
    if (!(await this.ensureSyncedBeforePdf())) {
      return;
    }

    // Create a deep copy of this.order so changes don't affect the original
    const ordercopy = JSON.parse(JSON.stringify(this.order));
    ordercopy.customerEmail = reciever;

    if (sendToCustomer) {
      // Check if email is present
      if (reciever === null || reciever === '') {
        this.alert.presentToast('Empfänger E-Mail nicht vorhanden');
        return;
      }

      // Check if email is valid
      const email = (await reciever.trim()) || '';
      if (this.isValidEmail(email) === false) {
        const corr = await this.alert.presentConfirmationAlert(
          'Ungültige E-Mail',
          ordercopy.customerEmail +
            ' enthält ein ungültiges Format. Trotzdem senden?'
        );
        if (corr === false) {
          return;
        }
      }

      // Question if report should be sent to customer
      const wee = await this.alert.presentConfirmationAlert(
        'Auftrag an Empfanger senden?',
        'Report an den Empfanger senden: ' + ordercopy.customerEmail
      );
      if (wee === false) {
        return;
      }
    }

    this.report.createSendToCustomer(ordercopy);
  }

  async sendwhole() {
    if (!(await this.ensureSyncedBeforePdf())) {
      return;
    }
    this.report.createWholeToAdmin(this.order);
  }

  isValidEmail(email: string) {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  async changeTitle() {
    this.current.changeTitle();
  }

  async changeInfo() {
    this.current.changeInfo();
  }

  async changeEmail() {
    this.current.changeEmail();
  }

  async changeDeliveryNote() {
    this.current.changeAgentNote();
  }

  async changeCompany() {
    this.current.changeCompany();
  }

  async changeAgentNote() {
    this.current.changeAgentNote();
  }

  async changeDeliveryRef() {
    this.current.changeDeliveryRef();
  }

  // Direkte Änderung von Order-Properties ohne Dialog
  onOrderPropertyChange(propertyName: keyof Order, value: any) {
    const order = this.order;
    if (order) {
      (order as any)[propertyName] = value;
      this.subscriberService.setCurrentOrder(order);
      this.cdr.markForCheck();
    }
  }

  // Änderung von Rechnungsadresse-Feldern
  onBillingAddressChange(field: string, value: any) {
    const order = this.order;
    if (order && order.customerAddress) {
      (order.customerAddress as any)[field] = value;

      // Wenn der Name geändert wurde, aktualisiere den Titel
      if (field === 'name' && value && value.trim() !== '') {
        this.updateOrderTitleWithCustomerName(value);
      }

      this.subscriberService.setCurrentOrder(order);
      this.cdr.markForCheck();
    }
  }

  // Aktualisiere den Order-Titel mit dem Kundennamen
  updateOrderTitleWithCustomerName(customerName: string) {
    if (!this.order || !customerName) return;

    const currentTitle = this.order.orderTitle || '';

    // Prüfe, ob der Titel mit "Neuer Auftrag vom" beginnt
    if (currentTitle.startsWith('Neuer Auftrag vom')) {
      // Extrahiere das Datum (alles nach "Neuer Auftrag vom ")
      const dateMatch = currentTitle.match(/Neuer Auftrag vom (.+)/);
      if (dateMatch && dateMatch[1]) {
        // Ersetze "Neuer Auftrag vom" durch den Kundennamen
        this.order.orderTitle = `${customerName} - ${dateMatch[1]}`;
        this.cdr.markForCheck();
      }
    }
  }

  // Änderung von Lieferadresse-Feldern
  onDeliveryAddressChange(field: string, value: any) {
    const order = this.order;
    if (order && order.customerBase && order.customerBase.delivery) {
      (order.customerBase.delivery as any)[field] = value;
      this.subscriberService.setCurrentOrder(order);
      this.cdr.markForCheck();
    }
  }

  async archiveOrder() {
    this.current.archiveOrdner();
  }

  downloadOrderAsJson() {
    if (!this.order) return;
    const blob = new Blob([JSON.stringify(this.order, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `order-${this.order.orderId}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async finishOrder() {
    this.current.finishOrder();
  }

  async reopenOrder() {
    await this.current.reopenOrder();
  }

  async changeOrderOwner() {
    if (!this.order || !this.isAdmin()) return;
    await this.orderService.changeOrderOwner(this.order);
    this.cdr.markForCheck();
  }

  /**
   * Check if the current user is the owner of the order
   * @returns true if the user is the owner of the order
   */
  isOrderOwner(): boolean {
    if (!this.login.decodedToken || !this.order?.owner) {
      return false;
    }

    if (this.order.owner === 'NEW') {
      return true;
    }

    const owner = this.order.owner;
    const ownerEmail = typeof owner === 'string' ? owner : owner?.email;
    return ownerEmail === this.login.decodedToken.email;
  }

  private getOrderState(): number | null {
    const raw = (this.order as any)?.state;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
  }

  private isOrderOpen(): boolean {
    const state = this.getOrderState();
    // null/undefined/0 => open
    return state === null || state === 0;
  }

  isOrderClosed(): boolean {
    return !this.isOrderOpen();
  }

  /**
   * Check if the current user is an admin
   * @returns true if the user is an admin
   */
  isAdmin(): boolean {
    return this.login.decodedToken?.role === 'admin';
  }

  /**
   * Check if the order can be finished
   * @returns true if order is open and user is allowed to finish it
   */
  canFinishOrder(): boolean {
    // Admins can finish also foreign orders.
    if (this.isAdmin()) {
      return this.isOrderOpen();
    }
    // Non-admins: only owner can finish, and only when open.
    return this.isOrderOwner() && this.isOrderOpen();
  }

  /**
   * Check if standard order actions should be visible
   * @returns true if user is admin (always) OR user is owner and order is open
   */
  showStandardActions(): boolean {
    // Admins can always edit orders, even closed ones
    if (this.isAdmin()) {
      return true;
    }
    
    // Owners can only edit open orders
    if (!this.isOrderOpen()) {
      return false;
    }
    return this.isOrderOwner();
  }

  /**
   * Check if force exit button should be visible
   * @returns true if user is not owner and order state is 0/null/undefined OR user is owner and order state is 1
   */
  showForceExit(): boolean {
    // Force exit when user cannot do standard actions.
    return !this.showStandardActions();
  }
}
